import React, { useState, useEffect, useContext } from "react";
import "./css/userProfile.css";
import { Form } from "react-bootstrap";
import CustomErrorMsg from "../errorMsg/CustomErrorMsg";
import { getRequest, patchRequest } from "../apiRequests/requestApi";
import BankBuilding from "../../assets/images/bank.svg";
import { BsBank } from "react-icons/bs";
import { FaUser } from "react-icons/fa";
import { RiNumbersFill } from "react-icons/ri";
import { UserContext } from "../../utils/contexts/UserContext";


export default function AddBank() {
  const { user } = useContext(UserContext);
  const [formData, setFormData] = useState({
    bankCode: "",
    accountNumber: "",
    accountName: ""
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verificationStep, setVerificationStep] = useState("input"); // input, verifying, verified
  const [bankList, setBankList] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    async function fetchBanks() {
      try {
        const response = await getRequest("/payment/banks");
        const data = response?.data;
        if (Array.isArray(data)) {
          setBankList(data);
        } else if (Array.isArray(data.banks)) {
          setBankList(data.banks);
        } else {
          setBankList([]);
        }
      } catch (err) {
        setBankList([]);
      }
    }
    fetchBanks();
  }, []);

  useEffect(() => {
    // Only set formData and verificationStep if user has bank details and not editing, and only if not already set
    if (
      user &&
      user.bank_name &&
      user.account_number &&
      user.account_name &&
      !isEditing &&
      bankList.length > 0 &&
      verificationStep !== "verified"
    ) {
      setFormData({
        bankCode: bankList.find(b => b.name === user.bank_name)?.code || "",
        accountNumber: user.account_number,
        accountName: user.account_name
      });
      setVerificationStep("verified");
    }
  }, [user, bankList, isEditing, verificationStep]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const verifyAccount = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const patchData = {
        account_number: formData.accountNumber,
        account_name: formData.accountName,
        bank_name: bankList.find(bank => bank.code === formData.bankCode)?.name || ""
      };
      // PATCH profile with bank details BEFORE verification
      const patchResponse = await patchRequest({
        url: "/auth/profile",
        detail: patchData
      });
      if (!patchResponse.success) {
        setError(patchResponse.message || "Failed to save bank details before verification.");
        setLoading(false);
        return;
      }
      // Now verify the account details with the GET request
      const response = await getRequest(`/payment/details/bank/${formData.bankCode}/acc/${formData.accountNumber}`);
      if (response && response.success) {
        setFormData(prev => ({
          ...prev,
          verified: true
        }));
        setVerificationStep("verified");
        setIsEditing(false);
        setError("");
      } else {
        setError(response?.message || "Failed to verify account. Please check the details and try again.");
      }
    } catch (error) {
      setError("An error occurred while verifying the account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      setLoading(true);
      const patchData = {
        account_number: formData.accountNumber,
        account_name: formData.accountName,
        bank_name: bankList.find(bank => bank.code === formData.bankCode)?.name || ""
      };
      const patchResponse = await patchRequest({
        url: "/auth/profile",
        detail: patchData
      });
      if (patchResponse.success) {
        // Try the details again
        const response = await getRequest(`/payment/details/bank/${formData.bankCode}/acc/${formData.accountNumber}`);
        if (response.success) {
          // Optionally show a success message or redirect
        } else {
          setError("Bank details saved, but could not re-verify. Please try again.");
        }
      } else {
        throw new Error(patchResponse.message || "Failed to save bank details");
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard py-lg-5 py-md-5 my-md-3 my-lg-3 my-0 py-2">
      <div className="d-flex flex-column align-items-center justify-content-center">
        <div className="bank-icon-container mb-4">
          <img 
            src={BankBuilding} 
            alt="Bank Building"
            className="bank-building-icon"
          />
        </div>

        <div className="col-lg-6 col-md-8 col-12">
          {error && (
            <div className="mb-4">
              <CustomErrorMsg errorMsg={error} />
            </div>
          )}

          <Form className="add-bank-form">
            <div className="mb-4">
              <Form.Select
                name="bankCode"
                value={formData.bankCode}
                onChange={handleInputChange}
                className="form-control register-input-container py-3"
                disabled={verificationStep === "verifying" || verificationStep === "verified"}
              >
                <option value="">Select Bank</option>
                {bankList.map(bank => (
                  <option key={bank.code} value={bank.code}>
                    {bank.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="mb-4">
              <input
                type="text"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleInputChange}
                placeholder="Account Number"
                maxLength={10}
                className="form-control register-input-container py-3"
                disabled={verificationStep === "verifying" || verificationStep === "verified"}
              />
            </div>

            <div className="mb-4">
              <input
                type="text"
                name="accountName"
                value={formData.accountName}
                onChange={handleInputChange}
                placeholder="Account Name"
                className="form-control register-input-container py-3"
                disabled={verificationStep === "verifying" || verificationStep === "verified"}
              />
            </div>

            {verificationStep === "input" && (
              <button
                onClick={verifyAccount}
                disabled={loading || !formData.bankCode || !formData.accountNumber || !formData.accountName}
                className="w-100 bg-warning text-dark d-flex align-items-center justify-content-center p-3 mb-4"
              >
                <p className="p-0 m-0 font-weight-500 font-family-poppins">
                  {loading ? "Verifying..." : "Verify"}
                </p>
              </button>
            )}

            {(verificationStep === "verified" || (user && user.bank_name && user.account_number && user.account_name)) && (
              <div className="verified-account-details mb-4">
                <div className="detail-card">
                  <div className="detail-item">
                    <div className="detail-icon">
                      <BsBank size={20} />
                    </div>
                    <div className="detail-content">
                      <label>Bank Name</label>
                      <p>{bankList.find(bank => bank.code === formData.bankCode)?.name}</p>
                    </div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-icon">
                      <FaUser size={20} />
                    </div>
                    <div className="detail-content">
                      <label>Account Name</label>
                      <p>{formData.accountName}</p>
                    </div>
                  </div>
                  
                  <div className="detail-item">
                    <div className="detail-icon">
                      <RiNumbersFill size={20} />
                    </div>
                    <div className="detail-content">
                      <label>Account Number</label>
                      <p>{formData.accountNumber}</p>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="mt-3 w-full rounded shadow font-bold bg-[#882291] text-white py-3 transition-colors duration-200 hover:bg-[#882291] disabled:opacity-60"
                  onClick={() => { setVerificationStep("input"); setIsEditing(true); }}
                  disabled={loading}
                >
                  Update Details
                </button>
              </div>
            )}

            {verificationStep === "verified" && (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-100 bg-success text-white d-flex align-items-center justify-content-center p-3"
              >
                <p className="p-0 m-0 font-weight-500 font-family-poppins">
                  {loading ? "Processing..." : "Continue"}
                </p>
              </button>
            )}
          </Form>
        </div>
      </div>
    </div>
  );
}